import { UserProfileRole } from '@@types/api.types';
import { UserProfileComponentProps } from '@components/pages/home/components/home-user-profile-modal.component';
import { ErrorMessage } from '@components/shared/error-message.component';
import { FormField } from '@components/shared/form-field.component';
import { FormLabel } from '@components/shared/form-label.component';
import { PopoverSelector } from '@components/shared/popover-selector.component';
import { MultiSelect } from '@components/ui/multi-select';
import { useGetCategories } from '@queries/category.query';
import { useGetJobRoles } from '@queries/job-roles.query';
import { useInnovatorCreateResearchKeyword, useInnovatorGetResearchKeywords } from '@queries/research-keywords.query';
import { FunctionComponent, useMemo } from 'react';

export const SkillsStep: FunctionComponent<UserProfileComponentProps> = (props) => {
  const { errors, formData, setFormData } = props;

  const isInnovator = formData.role === UserProfileRole.INNOVATOR;

  const { data: categories = [], isLoading: isLoadingCategories } = useGetCategories();
  const { data: jobRoles = [], isLoading: isLoadingJobRoles } = useGetJobRoles();
  const { data: researchKeywords = [], isLoading: isLoadingKeywords } = useInnovatorGetResearchKeywords();
  const { mutateAsync: createKeyword, isPending: isCreatingKeyword } = useInnovatorCreateResearchKeyword();

  const subcategoryOptions = useMemo(() => {
    if (!formData.categoryId) return [];
    const selectedCategory = categories.find((c) => c.id === formData.categoryId);
    return selectedCategory?.subcategories ?? [];
  }, [categories, formData.categoryId]);

  const handleJobRoleChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      jobRoleId: value || undefined,
    }));
  };

  const handleCategoryChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      categoryId: value || undefined,
      subcategoryId: undefined,
    }));
  };

  const handleSubcategoryChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      subcategoryId: value || undefined,
    }));
  };

  const handleKeywordsChange = (selectedIds: string[]) => {
    setFormData((prev) => ({
      ...prev,
      researchKeywordIds: selectedIds,
    }));
  };

  const handleCreateKeyword = async (keywordName: string) => {
    const createdKeyword = await createKeyword({ name: keywordName });
    const currentIds = formData.researchKeywordIds ?? [];
    setFormData((prev) => ({
      ...prev,
      researchKeywordIds: [...currentIds, createdKeyword.id],
    }));
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 px-3">
      <FormField>
        <PopoverSelector
          className="w-full"
          disabled={isLoadingJobRoles}
          emptyMessage="No job roles found"
          hasError={!!errors.jobRoleId}
          label="Job Role"
          options={jobRoles}
          placeholder="Select job role"
          searchPlaceholder="Search job roles..."
          setValue={handleJobRoleChange}
          value={formData.jobRoleId}
        />
        <ErrorMessage zodError={errors.jobRoleId} />
      </FormField>

      <div className="flex w-full flex-row items-start gap-4">
        <FormField className="min-w-0 flex-1">
          <FormLabel
            htmlFor="categoryId"
            label="Category"
          />
          <PopoverSelector
            className="w-full"
            disabled={isLoadingCategories}
            emptyMessage="No categories found"
            hasError={!!errors.categoryId}
            options={categories}
            placeholder="Select category"
            searchPlaceholder="Search categories..."
            setValue={handleCategoryChange}
            value={formData.categoryId}
          />
          <ErrorMessage zodError={errors.categoryId} />
        </FormField>

        <FormField className="min-w-0 flex-1">
          <FormLabel
            htmlFor="subcategoryId"
            label="Subcategory"
          />
          <PopoverSelector
            className="w-full"
            disabled={!formData.categoryId || isLoadingCategories}
            emptyMessage="No subcategories found"
            onClear={() => handleSubcategoryChange('')}
            options={subcategoryOptions}
            placeholder="Select subcategory"
            searchPlaceholder="Search subcategories..."
            setValue={handleSubcategoryChange}
            value={formData.subcategoryId}
          />
          <ErrorMessage zodError={errors.subcategoryId} />
        </FormField>
      </div>

      {isInnovator && (
        <FormField>
          <FormLabel
            htmlFor="researchKeywords"
            label="Research Keywords"
          />
          <MultiSelect
            className="bg-gray-100"
            disabled={isLoadingKeywords}
            isCreating={isCreatingKeyword}
            onCreateOption={handleCreateKeyword}
            onValueChange={handleKeywordsChange}
            options={researchKeywords}
            placeholder="Select or create keywords..."
            value={formData.researchKeywordIds ?? []}
          />
          <ErrorMessage zodError={errors.researchKeywordIds} />
        </FormField>
      )}
    </div>
  );
};
